import React from 'react';
import { Typography } from '@mui/material';
import ResourceDetailHeader from 'js/components/ResourceDetailHeader/ResourceDetailHeader';
import { updateSourceInfo } from 'js/actions';
import { Stack } from 'new/elements/Stack';
import { RESOURCE_TYPE_SOURCE } from 'js/constants/resourceTypes';
import styles from './DataSourceDetailHeader.module.scss';
import DataSourceDetailActionsContainer from 'js/components/data_sources/cards/DataSourceDetailActionsContainer';
import ViewEntityPipelineButtonContainer from 'js/components/pipelines/cards/ViewEntityPipelineButtonContainer';
import { SearchFilterTypes } from 'js/constants/visibilityFilters';
import UIUtils from 'js/lib/UIUtils';
import PipelineStatusBadge, {
  Status,
} from 'new/components/pipelines/PipelineStatusChip/PipelineStatusChip';
import { ChipWithCopy } from 'new/elements/ChipWithCopy';
import useAppDispatch from 'js/hooks/useAppDispatch';

interface DataSourceDetailHeaderProps {
  readOnly: boolean;
  adminAccess: boolean;
  hideActions: boolean;
  data: any;
  isManaged: any;
  connectionOrVendorType: any;
  showPipelineButton?: boolean;
  onCloseDrawer?(): void;
}

const Connection = ResourceDetailHeader.TopContent.Connection;
const ViewFlowButton = ResourceDetailHeader.TopContent.ViewFlowButton;

export const DataSourceDetailHeader = (props: DataSourceDetailHeaderProps) => {
  const {
    readOnly,
    hideActions,
    adminAccess,
    data,
    isManaged,
    showPipelineButton = true,
    connectionOrVendorType,
  } = props;

  const dispatch = useAppDispatch();

  if (!data) return null;

  const updateInfo = (sourceID: number, info = {}) => {
    dispatch(updateSourceInfo(data.id, info));
  };

  const isQueryPrompt = data.node_type === 'query_prompt' || data.flow_type === 'rag';

  const isApiServer = data.node_type === 'api_server';

  const shouldHideActions = hideActions || !!isQueryPrompt || !!isApiServer;

  return (
    <ResourceDetailHeader
      readOnly={readOnly}
      adminAccess={adminAccess}
      data={data}
      updateInfo={updateInfo}
      onCloseDrawer={props?.onCloseDrawer}
    >
      <ResourceDetailHeader.TopContent>
        <Stack direction="row" alignItems="center" justifyContent="space-between" gap="16px">
          <Stack direction="row" alignItems="center" gap="16px">
            {isQueryPrompt ? (
              <ResourceDetailHeader.TopContent.ConnectionBox>
                <Typography>Query Prompt</Typography>
              </ResourceDetailHeader.TopContent.ConnectionBox>
            ) : isApiServer ? (
              <ResourceDetailHeader.TopContent.ConnectionBox>
                <Typography>API Server</Typography>
              </ResourceDetailHeader.TopContent.ConnectionBox>
            ) : (
              <Connection resourceType={RESOURCE_TYPE_SOURCE} data={data} />
            )}
            <ChipWithCopy tooltipTitle="Copy Source ID" toCopy={data.id}>
              ID {data.id}
            </ChipWithCopy>
            <PipelineStatusBadge
              id={data.id}
              status={data.status as Status}
              runtimeStatus={data?.runtime_status}
              ingestionMode={data.ingestion_mode}
              parentArchivalStatus={data?.archival_status}
            />
            {isQueryPrompt || isApiServer ? null : (
              <div className={styles.infoTile}>
                <Typography variant="body3">
                  {UIUtils.toPluralFormat('Nexset', data?.data_sets?.length || 0, true)}
                </Typography>
              </div>
            )}
          </Stack>
          <Stack direction="row" alignItems="center" gap="16px">
            {showPipelineButton && (
              <ViewEntityPipelineButtonContainer
                filterType={SearchFilterTypes.SOURCE_ID}
                filterValue={data.id}
                className={'view-entity-pipeline-button-position'}
                ButtonComponent={<ViewFlowButton />}
              />
            )}
            {shouldHideActions ? null : (
              <DataSourceDetailActionsContainer
                data={data}
                showStatus={false}
                readOnly={readOnly}
                isManaged={isManaged}
                connectionOrVendorType={connectionOrVendorType}
                hideMenuDetails={true}
              />
            )}
          </Stack>
        </Stack>
      </ResourceDetailHeader.TopContent>
    </ResourceDetailHeader>
  );
};
